/**
 * useFilters Hook
 * Manages filter state with optional URL synchronization
 * Supports default values, reset, and change tracking
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * useFilters hook
 *
 * @param {Object} defaultFilters - Default filter values
 * @param {Object} options - Configuration options
 * @param {boolean} options.syncWithUrl - Sync filters with URL query params (default: false)
 * @param {function} options.onChange - Callback when filters change
 * @returns {Object} { filters, setFilters, setFilter, resetFilters, hasActiveFilters }
 */
export default function useFilters(defaultFilters = {}, options = {}) {
  const { syncWithUrl = false, onChange } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL if syncWithUrl, otherwise use defaults
  const getInitialFilters = useCallback(() => {
    if (syncWithUrl) {
      const urlFilters = {};
      for (const key in defaultFilters) {
        const urlValue = searchParams.get(key);
        if (urlValue !== null) {
          // Parse value based on default type
          const defaultValue = defaultFilters[key];
          if (typeof defaultValue === 'number') {
            urlFilters[key] = Number(urlValue);
          } else if (typeof defaultValue === 'boolean') {
            urlFilters[key] = urlValue === 'true';
          } else if (Array.isArray(defaultValue)) {
            urlFilters[key] = urlValue.split(',');
          } else {
            urlFilters[key] = urlValue;
          }
        } else {
          urlFilters[key] = defaultFilters[key];
        }
      }
      return urlFilters;
    }
    return { ...defaultFilters };
  }, [defaultFilters, syncWithUrl, searchParams]);

  const [filters, setFiltersState] = useState(getInitialFilters);

  // Sync filters to URL when they change
  useEffect(() => {
    if (syncWithUrl) {
      const params = new URLSearchParams();
      for (const key in filters) {
        const value = filters[key];
        // Only add non-default values to URL
        if (value !== null && value !== undefined && value !== '' && value !== defaultFilters[key]) {
          if (Array.isArray(value)) {
            params.set(key, value.join(','));
          } else {
            params.set(key, String(value));
          }
        }
      }
      setSearchParams(params, { replace: true });
    }
  }, [filters, syncWithUrl, defaultFilters, setSearchParams]);

  // Call onChange callback when filters change
  useEffect(() => {
    if (onChange) {
      onChange(filters);
    }
  }, [filters, onChange]);

  // Set all filters at once
  const setFilters = useCallback((newFilters) => {
    if (typeof newFilters === 'function') {
      setFiltersState(prev => ({ ...prev, ...newFilters(prev) }));
    } else {
      setFiltersState(prev => ({ ...prev, ...newFilters }));
    }
  }, []);

  // Set individual filter
  const setFilter = useCallback((key, value) => {
    setFiltersState(prev => ({ ...prev, [key]: value }));
  }, []);

  // Reset to defaults
  const resetFilters = useCallback(() => {
    setFiltersState({ ...defaultFilters });
  }, [defaultFilters]);

  // Check if any filters are active (different from defaults)
  const hasActiveFilters = useMemo(() => {
    for (const key in filters) {
      const value = filters[key];
      const defaultValue = defaultFilters[key];

      if (Array.isArray(value) && Array.isArray(defaultValue)) {
        if (value.length !== defaultValue.length || !value.every((v, i) => v === defaultValue[i])) {
          return true;
        }
      } else if (value !== defaultValue && value !== null && value !== undefined && value !== '') {
        return true;
      }
    }
    return false;
  }, [filters, defaultFilters]);

  return {
    filters,
    setFilters,
    setFilter,
    resetFilters,
    hasActiveFilters,
  };
}
